/**
 * Notices when a newer build has been deployed, so a member can be offered a
 * reload instead of being left on week-old code.
 *
 * Why this is polled rather than driven by the service worker: `service-worker.js`
 * is a static file whose contents never change between deploys, so the browser
 * sees the same bytes, no `updatefound` fires, and the usual "new version
 * waiting" hook never runs. What does change every build is the hashed bundle
 * name in `asset-manifest.json`, so that is the stamp we watch.
 *
 * Nothing here reloads on its own. A member mid-message or mid-listing does not
 * want the page pulled out from under them, so this only raises a flag and the
 * banner asks.
 */

import { useCallback, useEffect, useRef, useState } from "react";

const MANIFEST = "/asset-manifest.json";
const POLL_MS = 10 * 60 * 1000;
// iOS fires visibilitychange on every app-switch, so a member flicking between
// apps would otherwise hammer this. One check a minute is plenty.
const MIN_GAP_MS = 60 * 1000;

const MAIN_BUNDLE = /\/static\/js\/main\.[^/]+\.js/;

/**
 * The bundle this session is actually running, read off the script tag rather
 * than remembered from the first poll. That distinction matters: if a stale
 * index.html were ever served, a remembered value would quietly match itself
 * forever and no update would ever be offered.
 */
function runningBundle() {
  const tags = Array.from(document.querySelectorAll("script[src]"));
  for (const tag of tags) {
    const match = (tag.getAttribute("src") || "").match(MAIN_BUNDLE);
    if (match) return match[0];
  }
  return null;
}

async function deployedBundle() {
  const res = await fetch(`${MANIFEST}?t=${Date.now()}`, { cache: "no-store" });
  if (!res.ok) return null;
  const json = await res.json();
  const match = (json?.files?.["main.js"] || "").match(MAIN_BUNDLE);
  return match ? match[0] : null;
}

export default function useAppUpdate() {
  const [updateReady, setUpdateReady] = useState(false);
  const lastCheck = useRef(0);

  const check = useCallback(async () => {
    // Development serves bundles unhashed and rebuilds constantly; this would
    // be noise at best and a reload loop at worst.
    if (process.env.NODE_ENV !== "production") return;
    if (Date.now() - lastCheck.current < MIN_GAP_MS) return;
    lastCheck.current = Date.now();

    const running = runningBundle();
    if (!running) return;

    try {
      const deployed = await deployedBundle();
      if (deployed && deployed !== running) setUpdateReady(true);
    } catch (e) {
      // Offline or the manifest is briefly missing mid-deploy. Try again later.
    }
  }, []);

  useEffect(() => {
    check();

    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };

    const timer = setInterval(check, POLL_MS);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [check]);

  const applyUpdate = useCallback(() => {
    // Navigating rather than reload() so the service worker's navigate handler
    // runs, which fetches index.html network-first and therefore picks up the
    // new bundle names.
    window.location.href = window.location.pathname + window.location.search;
  }, []);

  return { updateReady, applyUpdate, dismissUpdate: () => setUpdateReady(false) };
}
